import logoBrandStarter from '../assets/logo_brandstarter.png';
import { useState, useRef } from 'react';
import { api } from '../services/api';

// ─── STYLES ──────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

  :root {
    --id-green: #173B34;
    --id-orange: #E4572E;
    --id-cream: #F6F2EC;
    --id-dark: #222222;
    --id-line: #EDE9E2;
  }

  .mi { font-family: 'Inter', sans-serif; color: var(--id-dark); }

  .mi-progress { height: 3px; background: var(--id-line); border-radius: 2px; margin-bottom: 32px; overflow: hidden; }
  .mi-progress-fill { height: 100%; background: linear-gradient(90deg, var(--id-green), var(--id-orange)); border-radius: 2px; transition: width .4s ease; }

  /* FORMATION */
  .mi-formation { max-width: 720px; margin: 0 auto; }
  .mi-part { margin-bottom: 40px; }
  .mi-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 10.5px; font-weight: 700; letter-spacing: .14em;
    text-transform: uppercase; color: var(--id-orange); margin-bottom: 14px;
  }
  .mi-eyebrow::before { content: ''; width: 18px; height: 2px; background: var(--id-orange); display: inline-block; }
  .mi-part h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(20px, 2.5vw, 26px);
    color: var(--id-green); font-weight: 700;
    line-height: 1.2; margin-bottom: 14px;
  }
  .mi-part p { font-size: 15px; color: #555; line-height: 1.75; margin-bottom: 14px; font-weight: 300; }
  .mi-part strong { color: var(--id-dark); font-weight: 600; }

  .mi-hl {
    background: rgba(23,59,52,0.07); border-left: 3px solid var(--id-green);
    border-radius: 0 12px 12px 0; padding: 16px 20px; margin: 16px 0;
    font-size: 14.5px; color: #333; line-height: 1.65;
  }
  .mi-hl.orange { background: rgba(228,87,46,0.07); border-left-color: var(--id-orange); }

  .mi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 16px 0; }
  .mi-grid-item { background: #fff; border: 1px solid var(--id-line); border-radius: 12px; padding: 16px 18px; }
  .mi-grid-ico { font-size: 20px; margin-bottom: 8px; }
  .mi-grid-title { font-size: 13.5px; font-weight: 700; color: var(--id-green); margin-bottom: 5px; }
  .mi-grid-desc { font-size: 12.5px; color: #777; line-height: 1.55; }

  .mi-example { background: #fff; border: 1px solid var(--id-line); border-radius: 14px; overflow: hidden; margin: 16px 0; }
  .mi-ex-head { background: var(--id-cream); padding: 10px 18px; font-size: 11px; font-weight: 700; color: #999; letter-spacing: .1em; text-transform: uppercase; border-bottom: 1px solid var(--id-line); }
  .mi-ex-body { padding: 16px 18px; font-size: 14px; color: #444; line-height: 1.7; }

  .mi-vs { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 16px 0; }
  .mi-bad { background: rgba(220,38,38,0.05); border: 1px solid rgba(220,38,38,0.2); border-radius: 12px; padding: 14px 16px; }
  .mi-good { background: rgba(23,59,52,0.05); border: 1px solid rgba(23,59,52,0.2); border-radius: 12px; padding: 14px 16px; }
  .mi-vs-lbl { font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 8px; }
  .mi-bad .mi-vs-lbl { color: #DC2626; }
  .mi-good .mi-vs-lbl { color: var(--id-green); }
  .mi-vs-text { font-size: 13px; line-height: 1.6; color: #444; font-style: italic; }

  .mi-clara { display: flex; align-items: flex-start; gap: 14px; background: #fff; border: 1px solid var(--id-line); border-radius: 14px; padding: 18px; margin: 20px 0; }
  .mi-clara-av { width: 38px; height: 38px; border-radius: 50%; background: var(--id-green); display: flex; align-items: center; justify-content: center; font-family: 'Playfair Display', serif; font-weight: 700; font-size: 15px; color: var(--id-orange); flex-shrink: 0; }
  .mi-clara-name { font-size: 10.5px; font-weight: 700; color: var(--id-green); letter-spacing: .06em; text-transform: uppercase; margin-bottom: 5px; }
  .mi-clara-text { font-size: 13.5px; color: #444; line-height: 1.65; font-style: italic; font-family: 'Playfair Display', serif; }

  /* FORMULAIRE */
  .mi-form { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 18px; }
  .mi-field { background: #fff; border: 1px solid var(--id-line); border-radius: 14px; padding: 22px 24px; }
  .mi-field-num { font-size: 10.5px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--id-orange); margin-bottom: 8px; }
  .mi-field-label { font-size: 15px; font-weight: 600; color: var(--id-dark); margin-bottom: 6px; }
  .mi-field-hint { font-size: 13px; color: #888; line-height: 1.5; margin-bottom: 12px; }
  .mi-field textarea {
    width: 100%; padding: 12px 14px;
    border: 1.5px solid var(--id-line); border-radius: 10px;
    font-size: 14.5px; font-family: 'Inter', sans-serif; color: var(--id-dark);
    background: #FDFCFA; outline: none; resize: vertical; min-height: 80px;
    line-height: 1.6; transition: border-color .15s;
  }
  .mi-field textarea:focus { border-color: var(--id-green); background: #fff; box-shadow: 0 0 0 4px rgba(23,59,52,0.06); }
  .mi-field textarea::placeholder { color: #C0B8B0; }

  /* DIALOGUE IA */
  .mi-messages { display: flex; flex-direction: column; gap: 14px; margin-bottom: 18px; max-height: 440px; overflow-y: auto; }
  .mi-msg { display: flex; gap: 10px; align-items: flex-start; }
  .mi-msg.user { flex-direction: row-reverse; }
  .mi-msg-av { width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; }
  .mi-msg.clara .mi-msg-av { background: var(--id-green); color: var(--id-orange); font-family: 'Playfair Display', serif; font-size: 14px; }
  .mi-msg.user .mi-msg-av { background: var(--id-orange); color: #fff; }
  .mi-msg-bubble { max-width: 78%; padding: 13px 16px; font-size: 14px; line-height: 1.65; white-space: pre-wrap; }
  .mi-msg.clara .mi-msg-bubble { background: #fff; border: 1px solid var(--id-line); border-radius: 16px 16px 16px 4px; color: #333; }
  .mi-msg.user .mi-msg-bubble { background: var(--id-green); color: #F6F2EC; border-radius: 16px 16px 4px 16px; }

  .mi-input-wrap { display: flex; gap: 10px; align-items: flex-end; background: #fff; border: 1.5px solid var(--id-line); border-radius: 14px; padding: 12px 16px; transition: border-color .15s; }
  .mi-input-wrap:focus-within { border-color: var(--id-green); }
  .mi-input-wrap textarea { flex: 1; border: none; outline: none; resize: none; font-size: 14.5px; font-family: 'Inter', sans-serif; color: var(--id-dark); line-height: 1.6; background: transparent; min-height: 44px; max-height: 120px; }
  .mi-input-wrap textarea::placeholder { color: #C0B8B0; }
  .mi-send { background: var(--id-green); color: #F6F2EC; border: none; border-radius: 10px; padding: 10px 18px; font-size: 13.5px; font-weight: 700; font-family: 'Inter', sans-serif; cursor: pointer; flex-shrink: 0; align-self: flex-end; transition: background .15s; }
  .mi-send:hover { background: #0E2820; }
  .mi-send:disabled { opacity: .4; cursor: not-allowed; }

  /* FICHE */
  .fi2 { background: #fff; border-radius: 20px; overflow: hidden; box-shadow: 0 4px 6px rgba(23,59,52,0.04), 0 20px 40px rgba(23,59,52,0.10); margin-top: 20px; font-family: 'Inter', sans-serif; }
  .fi2-header { background: var(--id-green); padding: 26px 28px; position: relative; overflow: hidden; }
  .fi2-header::before { content: ''; position: absolute; top: -60px; right: -60px; width: 200px; height: 200px; border-radius: 50%; background: rgba(228,87,46,0.1); pointer-events: none; }
  .fi2-badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(228,87,46,0.2); border: 1px solid rgba(228,87,46,0.35); color: var(--id-orange); font-size: 10px; font-weight: 700; padding: 4px 12px; border-radius: 999px; letter-spacing: .1em; margin-bottom: 12px; position: relative; z-index: 2; }
  .fi2-name { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 700; color: #fff; margin-bottom: 4px; position: relative; z-index: 2; }
  .fi2-sub { font-size: 11px; color: rgba(246,242,236,0.4); letter-spacing: .08em; text-transform: uppercase; position: relative; z-index: 2; }

  .fi2-body { padding: 26px 28px; display: flex; flex-direction: column; gap: 16px; }
  .fi2-block { background: #FDFCFA; border: 1px solid var(--id-line); border-radius: 14px; overflow: hidden; }
  .fi2-block-head { background: var(--id-cream); padding: 10px 16px; border-bottom: 1px solid var(--id-line); display: flex; align-items: center; gap: 8px; }
  .fi2-block-ico { font-size: 14px; }
  .fi2-block-lbl { font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #AAA09A; }
  .fi2-block-body { padding: 14px 16px; font-size: 14px; color: #333; line-height: 1.7; }

  .fi2-vision { background: linear-gradient(135deg, var(--id-green), #1E4D44); border-radius: 14px; padding: 20px 22px; }
  .fi2-vision-lbl { font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: rgba(246,242,236,0.4); margin-bottom: 8px; }
  .fi2-vision-text { font-family: 'Playfair Display', serif; font-size: 16px; font-style: italic; line-height: 1.6; color: #F6F2EC; }

  .fi2-synthese { background: rgba(228,87,46,0.07); border: 1px solid rgba(228,87,46,0.2); border-radius: 14px; padding: 18px 20px; }
  .fi2-synthese-lbl { font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--id-orange); margin-bottom: 8px; }
  .fi2-synthese-text { font-size: 14px; color: #444; line-height: 1.7; }

  .fi2-footer { display: flex; justify-content: space-between; align-items: center; padding: 14px 28px; border-top: 1px solid var(--id-line); background: #FDFCFA; }
  .fi2-logo { height: 22px; width: auto; }
  .fi2-footer-txt { font-size: 10.5px; color: #C0B8B0; }
  .fi2-dl { background: var(--id-green); color: #F6F2EC; border: none; border-radius: 999px; padding: 9px 20px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: 'Inter', sans-serif; display: flex; align-items: center; gap: 7px; }

  /* BTNS */
  .mi-nav { display: flex; justify-content: space-between; align-items: center; margin-top: 32px; gap: 12px; }
  .mi-btn-primary { background: var(--id-green); color: #F6F2EC; border: none; border-radius: 999px; padding: 13px 28px; font-size: 14.5px; font-weight: 700; cursor: pointer; font-family: 'Inter', sans-serif; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 14px rgba(23,59,52,0.25); transition: all .15s; }
  .mi-btn-primary:hover { background: #0E2820; transform: translateY(-1px); }
  .mi-btn-primary:disabled { opacity: .4; cursor: not-allowed; transform: none; }
  .mi-btn-secondary { background: transparent; color: var(--id-green); border: 1.5px solid var(--id-line); border-radius: 999px; padding: 12px 24px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; }
  .mi-btn-secondary:hover { border-color: var(--id-green); }

  @media(max-width:640px) {
    .mi-grid { grid-template-columns: 1fr; }
    .mi-vs { grid-template-columns: 1fr; }
  }
`;

// ─── FORMATION ───────────────────────────────────────────────
const FORMATION = [
  {
    num: 'Partie 1',
    titre: "Pourquoi clarifier son idée avant tout le reste",
    content: () => (
      <>
        <p>Beaucoup de créateurs se lancent avec une idée floue et espèrent que ça se clarifiera en chemin. <strong>C'est rarement le cas.</strong> Plus tôt tu poses les bases, plus vite tout le reste s'aligne.</p>
        <div className="mi-hl orange">
          Une idée mal formulée produit une communication floue, une offre mal positionnée, et des clients qui ne comprennent pas pourquoi te choisir.
        </div>
        <p>Ce module te force à répondre aux 4 questions fondamentales que tout entrepreneur doit maîtriser sur le bout des doigts. Ce sont les mêmes questions qu'un investisseur, un client ou un partenaire te posera.</p>
        <div className="mi-grid">
          {[
            { ico: '💡', t: 'L\'idée', d: 'Ce que tu proposes en une phrase. Si tu ne peux pas l\'expliquer en 10 secondes, personne ne s\'en souviendra.' },
            { ico: '🎯', t: 'Le problème', d: 'Le problème réel que tu résous. Pas ton service, la douleur concrète de ton client.' },
            { ico: '🏆', t: 'Ta légitimité', d: 'Pourquoi toi et pas quelqu\'un d\'autre. Expérience, approche unique, histoire personnelle.' },
            { ico: '🚀', t: 'La vision', d: 'Où tu veux aller dans 3 ans. Pas un rêve flou, une direction concrète et inspirante.' },
          ].map((e, i) => (
            <div key={i} className="mi-grid-item">
              <div className="mi-grid-ico">{e.ico}</div>
              <div className="mi-grid-title">{e.t}</div>
              <div className="mi-grid-desc">{e.d}</div>
            </div>
          ))}
        </div>
      </>
    )
  },
  {
    num: 'Partie 2',
    titre: "L'idée : une phrase qui fait tout le travail",
    content: () => (
      <>
        <p>Ton idée doit tenir en une phrase. Pas un paragraphe. Une phrase que n'importe qui peut répéter à quelqu'un d'autre 5 minutes plus tard.</p>
        <div className="mi-example">
          <div className="mi-ex-head">📐 La formule qui marche</div>
          <div className="mi-ex-body">
            <strong>"Je propose [quoi] pour [qui] qui veulent [résultat]."</strong><br/><br/>
            Exemples :<br/>
            "Je propose des coachings en posture pour les télétravailleurs qui ont des douleurs chroniques."<br/>
            "Je propose des formations vidéo pour les artisans qui veulent trouver des clients sur Instagram."
          </div>
        </div>
        <div className="mi-vs">
          <div className="mi-bad">
            <div className="mi-vs-lbl">❌ Trop vague</div>
            <div className="mi-vs-text">"J'aide les gens à améliorer leur bien-être."</div>
          </div>
          <div className="mi-good">
            <div className="mi-vs-lbl">✅ Précis et mémorable</div>
            <div className="mi-vs-text">"J'accompagne les mamans solo dans la reprise d'activité après une reconversion."</div>
          </div>
        </div>
      </>
    )
  },
  {
    num: 'Partie 3',
    titre: "Le problème : parle de lui avant de parler de toi",
    content: () => (
      <>
        <p>Les clients n'achètent pas un service. Ils achètent une solution à un problème. <strong>Si tu ne nommes pas leur problème mieux qu'ils ne le font eux-mêmes, tu n'existes pas dans leur tête.</strong></p>
        <div className="mi-hl">
          Un bon entrepreneur connaît la douleur de son client mieux que ce dernier ne peut la formuler. C'est ce niveau de compréhension qui crée la confiance immédiate.
        </div>
        <div className="mi-vs">
          <div className="mi-bad">
            <div className="mi-vs-lbl">❌ Centré sur soi</div>
            <div className="mi-vs-text">"Je propose des services de comptabilité pour les indépendants."</div>
          </div>
          <div className="mi-good">
            <div className="mi-vs-lbl">✅ Centré sur le problème</div>
            <div className="mi-vs-text">"Les freelances perdent des heures chaque mois sur leur compta alors qu'ils pourraient les facturer."</div>
          </div>
        </div>
        <div className="mi-clara">
          <div className="mi-clara-av">C</div>
          <div>
            <div className="mi-clara-name">Clara · Guide BrandStarter</div>
            <div className="mi-clara-text">Quand je te demande quel problème tu résous, je veux entendre ton client parler, pas toi. Quels mots exact utilise-t-il pour décrire ce qui le bloque ?</div>
          </div>
        </div>
      </>
    )
  },
  {
    num: 'Partie 4',
    titre: "Ta légitimité : ce qui te rend unique et crédible",
    content: () => (
      <>
        <p>La légitimité ne vient pas forcément d'un diplôme ou de 20 ans d'expérience. Elle vient de <strong>ce que tu as vécu, appris ou compris que d'autres n'ont pas.</strong></p>
        <div className="mi-grid">
          {[
            { ico: '📚', t: 'L\'expérience', d: 'Années dans le secteur, clients accompagnés, résultats obtenus. Des chiffres si possible.' },
            { ico: '🔄', t: 'Le parcours', d: 'Tu as vécu le problème de ton client. Tu parles d\'expérience, pas de théorie.' },
            { ico: '🛠️', t: 'La méthode', d: 'Tu as développé une approche unique, un process qui te différencie.' },
            { ico: '💎', t: 'Le positionnement', d: 'Tu es le seul à combiner cette expertise avec ce contexte ou cette cible.' },
          ].map((e, i) => (
            <div key={i} className="mi-grid-item">
              <div className="mi-grid-ico">{e.ico}</div>
              <div className="mi-grid-title">{e.t}</div>
              <div className="mi-grid-desc">{e.d}</div>
            </div>
          ))}
        </div>
        <div className="mi-hl orange">
          Tu n'as pas besoin d'être le meilleur au monde. Tu as besoin d'être la meilleure option pour une personne précise avec un problème précis.
        </div>
      </>
    )
  },
  {
    num: 'Partie 5',
    titre: "Comment répondre aux 4 questions pour un maximum d'impact",
    content: () => (
      <>
        <p>Tu vas maintenant répondre aux 4 questions fondamentales. Clara analysera tes réponses et te challengera si elles sont trop vagues ou trop génériques.</p>
        <div className="mi-clara">
          <div className="mi-clara-av">C</div>
          <div>
            <div className="mi-clara-name">Clara · Guide BrandStarter</div>
            <div className="mi-clara-text">Je vais lire tes 4 réponses d'un bloc. Si quelque chose est flou, je te le dirai directement. L'objectif : que tu puisses réciter ces 4 réponses sans hésitation lors de n'importe quelle rencontre professionnelle.</div>
          </div>
        </div>
        <div className="mi-vs">
          <div className="mi-bad">
            <div className="mi-vs-lbl">❌ Vision inutilisable</div>
            <div className="mi-vs-text">"Dans 3 ans, je veux avoir une belle entreprise qui marche bien et être reconnu."</div>
          </div>
          <div className="mi-good">
            <div className="mi-vs-lbl">✅ Vision qui inspire</div>
            <div className="mi-vs-text">"Dans 3 ans, j'accompagne 50 indépendantes par an dans leur reconversion avec une équipe de 3 coachs certifiés."</div>
          </div>
        </div>
        <div className="mi-hl">
          <strong>La règle :</strong> si ta réponse pourrait s'appliquer à 100 autres entrepreneurs, recommence. La précision, c'est ce qui crée la différence.
        </div>
      </>
    )
  }
];

const QUESTIONS = [
  {
    label: 'Décris ton idée en une phrase simple',
    hint: 'Utilise la formule : "Je propose [quoi] pour [qui] qui veulent [résultat]." Sois précis.',
    placeholder: 'Je propose des ateliers de cuisine végétale pour les familles actives qui veulent manger sainement sans y passer des heures...',
  },
  {
    label: 'Quel problème concret est-ce que tu résous ?',
    hint: 'Décris le problème du point de vue de ton client, avec ses mots. Quelle douleur concrète ressent-il ?',
    placeholder: 'Les familles manquent de temps et d\'idées de repas sains. Elles finissent par commander des plats industriels par défaut...',
  },
  {
    label: 'Pourquoi toi et pas quelqu\'un d\'autre ?',
    hint: 'Expérience, parcours personnel, méthode unique, combinaison rare de compétences. Sois concret.',
    placeholder: 'J\'ai 10 ans d\'expérience en nutrition et j\'ai moi-même transformé l\'alimentation de ma famille avec une méthode en 20 minutes par jour...',
  },
  {
    label: 'Ta vision dans 3 ans',
    hint: 'Pas un rêve flou, une direction concrète. Qui accompagnes-tu ? Combien ? Comment ?',
    placeholder: 'Une école de cuisine végétale reconnue avec 5 formateurs, accompagnant 200 familles par an et un livre publié...',
  },
];

// ─── PARSER FICHE IDÉE ───────────────────────────────────────
function parseFicheIdee(synthese) {
  if (!synthese) return null;
  const r = { nomMarque:'', idee:'', probleme:'', legitimite:'', vision:'', synthese:'' };
  try {
    const keys = ['NOM_MARQUE','IDEE','PROBLEME_RESOLU','LEGITIMITE','VISION_3ANS','SYNTHESE'];
    let normalized = synthese;
    keys.forEach(k => { normalized = normalized.replace(new RegExp('\\s+' + k + ':', 'g'), '\n' + k + ':'); });
    normalized.split('\n').forEach(line => {
      const t = line.trim();
      const get = (k) => t.startsWith(k) ? t.slice(k.length).trim() : null;
      if (get('NOM_MARQUE:')) r.nomMarque = get('NOM_MARQUE:');
      if (get('IDEE:')) r.idee = get('IDEE:');
      if (get('PROBLEME_RESOLU:')) r.probleme = get('PROBLEME_RESOLU:');
      if (get('LEGITIMITE:')) r.legitimite = get('LEGITIMITE:');
      if (get('VISION_3ANS:')) r.vision = get('VISION_3ANS:');
      if (get('SYNTHESE:')) r.synthese = get('SYNTHESE:');
    });
  } catch(e) {}
  return r.nomMarque || r.idee ? r : null;
}

// ─── FICHE IDÉE V2 ───────────────────────────────────────────
function FicheIdeeV2({ synthese }) {
  const ficheRef = useRef(null);
  const data = parseFicheIdee(synthese);
  if (!data) return null;

  const handleDownload = () => {
    const el = ficheRef.current;
    if (!el) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    document.head.appendChild(script);
    script.onload = () => {
      window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(canvas => {
        const link = document.createElement('a');
        link.download = `idee-vision-${(data.nomMarque||'projet').toLowerCase().replace(/\s+/g,'-')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    };
  };

  const blocks = [
    { ico: '💡', lbl: "L'idée", val: data.idee },
    { ico: '🎯', lbl: 'Problème résolu', val: data.probleme },
    { ico: '🏆', lbl: 'Pourquoi toi', val: data.legitimite },
  ].filter(b => b.val);

  return (
    <div ref={ficheRef} className="fi2">
      <div className="fi2-header">
        <div className="fi2-badge">MODULE 01</div>
        <div className="fi2-name">{data.nomMarque || 'Mon projet'}</div>
        <div className="fi2-sub">Idée & Vision · BrandStarter</div>
      </div>

      <div className="fi2-body">
        {blocks.map((b, i) => (
          <div key={i} className="fi2-block">
            <div className="fi2-block-head">
              <span className="fi2-block-ico">{b.ico}</span>
              <span className="fi2-block-lbl">{b.lbl}</span>
            </div>
            <div className="fi2-block-body">{b.val}</div>
          </div>
        ))}

        {data.vision && (
          <div className="fi2-vision">
            <div className="fi2-vision-lbl">🚀 Vision 3 ans</div>
            <div className="fi2-vision-text">{data.vision}</div>
          </div>
        )}

        {data.synthese && (
          <div className="fi2-synthese">
            <div className="fi2-synthese-lbl">Synthèse Clara</div>
            <div className="fi2-synthese-text">{data.synthese}</div>
          </div>
        )}
      </div>

      <div className="fi2-footer">
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <img src={logoBrandStarter} alt="BrandStarter" className="fi2-logo" />
          <span className="fi2-footer-txt">Module 01 · Idée & Vision</span>
        </div>
        <button className="fi2-dl" onClick={handleDownload}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 7l3 3 3-3M3 13h10" stroke="#F6F2EC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Télécharger PNG
        </button>
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────
export function ModuleIdee({ moduleValide, syntheseExistante, onValidate, contextePrecedent }) {
  const [phase, setPhase] = useState(moduleValide ? 'done' : 'formation');
  const [formationStep, setFormationStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [conversation, setConversation] = useState([]);
  const [input, setInput] = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [formSoumis, setFormSoumis] = useState(false);
  const [synthese, setSynthese] = useState(syntheseExistante || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);

  const totalParts = FORMATION.length;
  const progress = phase === 'formation'
    ? ((formationStep + 1) / (totalParts + 2)) * 100
    : phase === 'formulaire' ? ((totalParts + 1) / (totalParts + 2)) * 100
    : phase === 'dialogue' ? ((totalParts + 1.5) / (totalParts + 2)) * 100
    : 100;

  const envoyerFormulaire = async () => {
    const manquant = QUESTIONS.some((_, i) => !answers[i] || answers[i].trim().length < 10);
    if (manquant) { setError('Réponds à toutes les questions avant de continuer.'); return; }
    setError('');

    const messageInitial = QUESTIONS.map((q, i) => `${q.label} :\n${answers[i]}`).join('\n\n');
    const userMsg = { role: 'user', content: messageInitial };
    setConversation([userMsg]);
    setFormSoumis(true);
    setPhase('dialogue');
    setIaLoading(true);
    try {
      const res = await api.chatIA(1, {
        conversation: [userMsg],
        tourActuel: 0,
        contextePrecedent
      });
      setConversation(prev => [...prev, { role: 'assistant', content: res.message }]);
      if (res.valide) { setSynthese(res.message); setPhase('done'); }
    } catch(e) { setError(e.message); }
    finally { setIaLoading(false); }
  };

  const sendMessage = async () => {
    if (!input.trim() || iaLoading) return;
    const userMsg = { role: 'user', content: input.trim() };
    const newConv = [...conversation, userMsg];
    setConversation(newConv);
    setInput('');
    setIaLoading(true);
    try {
      const res = await api.chatIA(1, { conversation: newConv, tourActuel: conversation.length, contextePrecedent });
      setConversation(prev => [...prev, { role: 'assistant', content: res.message }]);
      if (res.valide) { setSynthese(res.message); setPhase('done'); }
    } catch(e) { setError(e.message); }
    finally { setIaLoading(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="mi">
      <style>{CSS}</style>

      <div className="mi-progress">
        <div className="mi-progress-fill" style={{width:`${progress}%`}}></div>
      </div>

      {/* ── FORMATION ── */}
      {phase === 'formation' && (
        <div className="mi-formation">
          {FORMATION.map((part, idx) => (
            idx <= formationStep && (
              <div key={idx} className="mi-part">
                <div className="mi-eyebrow">{part.num}</div>
                <h2>{part.titre}</h2>
                {part.content()}
              </div>
            )
          ))}
          <div className="mi-nav">
            {formationStep > 0
              ? <button className="mi-btn-secondary" onClick={() => setFormationStep(s => s - 1)}>← Précédent</button>
              : <div/>
            }
            {formationStep < FORMATION.length - 1 ? (
              <button className="mi-btn-primary" onClick={() => setFormationStep(s => s + 1)}>
                Partie suivante
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="#F6F2EC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            ) : (
              <button className="mi-btn-primary" onClick={() => setPhase('formulaire')}>
                Répondre aux 4 questions
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="#F6F2EC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── FORMULAIRE ── */}
      {phase === 'formulaire' && (
        <div className="mi-form">
          {QUESTIONS.map((q, i) => (
            <div key={i} className="mi-field">
              <div className="mi-field-num">Question {i + 1} / {QUESTIONS.length}</div>
              <div className="mi-field-label">{q.label}</div>
              <div className="mi-field-hint">{q.hint}</div>
              <textarea
                value={answers[i] || ''}
                onChange={e => setAnswers(a => ({...a, [i]: e.target.value}))}
                placeholder={q.placeholder}
                rows={3}
              />
            </div>
          ))}
          {error && <div style={{color:'#DC2626',fontSize:13}}>{error}</div>}
          <div className="mi-nav">
            <button className="mi-btn-secondary" onClick={() => setPhase('formation')}>← Revoir le cours</button>
            <button className="mi-btn-primary" onClick={envoyerFormulaire} disabled={iaLoading}>
              Soumettre à Clara
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="#F6F2EC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* ── DIALOGUE IA ── */}
      {phase === 'dialogue' && (
        <div>
          <div className="mi-messages">
            {conversation.map((msg, i) => (
              <div key={i} className={`mi-msg ${msg.role === 'assistant' ? 'clara' : 'user'}`}>
                <div className="mi-msg-av">{msg.role === 'assistant' ? 'C' : '✎'}</div>
                <div className="mi-msg-bubble">{msg.content}</div>
              </div>
            ))}
            {iaLoading && (
              <div className="mi-msg clara">
                <div className="mi-msg-av">C</div>
                <div className="mi-msg-bubble" style={{color:'#AAA'}}>Clara analyse tes réponses...</div>
              </div>
            )}
            <div ref={chatEndRef}/>
          </div>
          {error && <div style={{color:'#DC2626',fontSize:13,marginBottom:12}}>{error}</div>}
          <div className="mi-input-wrap">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Écris ta réponse à Clara"
              rows={2}
              disabled={iaLoading}
            />
            <button className="mi-send" onClick={sendMessage} disabled={iaLoading || !input.trim()}>Envoyer</button>
          </div>
        </div>
      )}

      {/* ── FICHE ── */}
      {phase === 'done' && (
        <div>
          <FicheIdeeV2 synthese={synthese} />
          <div className="mi-nav" style={{marginTop:24}}>
            <button className="mi-btn-secondary" onClick={() => { setPhase('formulaire'); setFormSoumis(false); setConversation([]); setSynthese(''); }}>
              Recommencer
            </button>
            <button className="mi-btn-primary" onClick={() => onValidate(synthese)} disabled={saving}>
              {saving ? 'Enregistrement...' : 'Valider et continuer →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
